<script setup lang="ts">
import type { ScrollAreaRootProps } from 'reka-ui'
import type { HTMLAttributes } from 'vue'
import { computed, onBeforeUnmount, provide, ref, watch } from 'vue'
import { reactiveOmit } from '@vueuse/core'
import { ScrollAreaCorner, ScrollAreaRoot, ScrollAreaViewport } from 'reka-ui'
import { cn } from '@/lib/utils'
import ScrollBar from './ScrollBar.vue'
import { scrollAreaHorizontalOwner } from './context'

const props = defineProps<
  ScrollAreaRootProps & {
    class?: HTMLAttributes['class']
    hideHorizontalScrollbar?: boolean
    verticalScrollbarClass?: HTMLAttributes['class']
    verticalThumbClass?: HTMLAttributes['class']
    viewportClass?: HTMLAttributes['class']
    contentClass?: HTMLAttributes['class']
    viewportRef?: (element: HTMLDivElement | null) => void
  }
>()
const emit = defineEmits<{
  scroll: [event: Event]
}>()

const delegatedProps = reactiveOmit(
  props,
  'class',
  'hideHorizontalScrollbar',
  'verticalScrollbarClass',
  'verticalThumbClass',
  'viewportClass',
  'contentClass',
  'viewportRef',
)

const viewport = ref<InstanceType<typeof ScrollAreaViewport> | null>(null)
provide(
  scrollAreaHorizontalOwner,
  computed(() => !props.hideHorizontalScrollbar),
)
watch(
  () => [viewport.value?.viewportElement, props.viewportRef] as const,
  ([element, assign]) => assign?.((element as HTMLDivElement | undefined) ?? null),
  { flush: 'post' },
)
onBeforeUnmount(() => props.viewportRef?.(null))
</script>

<template>
  <ScrollAreaRoot
    data-slot="scroll-area"
    v-bind="delegatedProps"
    :class="cn('relative flex min-h-0 flex-col overflow-hidden', props.class)"
  >
    <ScrollAreaViewport
      ref="viewport"
      as-child
      data-slot="scroll-area-viewport"
      :class="
        cn(
          'w-full min-h-0 min-w-0 flex-1 max-h-[inherit] rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-3 focus-visible:ring-ring/50 focus-visible:outline-1',
          props.hideHorizontalScrollbar && '[&>div]:!w-full [&>div]:!min-w-0 [&>div]:!max-w-full',
          props.viewportClass,
        )
      "
      @scroll.passive="emit('scroll', $event)"
    >
      <div :class="props.contentClass"><slot /></div>
    </ScrollAreaViewport>
    <ScrollBar :class="props.verticalScrollbarClass" :thumb-class="props.verticalThumbClass" />
    <ScrollBar v-if="!props.hideHorizontalScrollbar" orientation="horizontal" />
    <ScrollAreaCorner v-if="!props.hideHorizontalScrollbar" />
  </ScrollAreaRoot>
</template>
